import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, MenuItem, TextField, Typography,
} from '@mui/material';
import documentSeriesService from '../../service/documentSeries.service';
import { exportInvoiceXml } from '../../service/invoiceXml.service';
import { downloadFile } from '../../utils/download';
import { DocumentType } from '../../config/DocumentType';

const ranges = [
  ['issueDate', 'Számla kelte', 'date'],
  ['docDate', 'Teljesítés dátuma', 'date'],
  ['docNum', 'Számlaszám', 'text'],
];

const InvoiceXmlExportPage = () => {
  const [series, setSeries] = useState([]);
  const [values, setValues] = useState({ documentSeriesId: '' });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    Promise.all([DocumentType.ARInvoiceManual, DocumentType.ARInvoice, DocumentType.ARInvoiceModification, DocumentType.DownPayment]
      .map((docTypeId) => documentSeriesService.getOptions(docTypeId)))
      .then((lists) => {
        if (active) setSeries([...new Map(lists.flat().map((item) => [item.value, item])).values()]);
      })
      .catch(() => { if (active) setError('A számlasorozatok betöltése nem sikerült.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const field = (name, label, type = 'text', required = false) => (
    <TextField key={name} label={label} type={type} required={required} fullWidth size="small"
      value={values[name] || ''} onChange={(event) => setValues((current) => ({ ...current, [name]: event.target.value }))}
      InputLabelProps={{ shrink: true }} disabled={busy} />
  );

  const handleExport = async (event) => {
    event.preventDefault();
    if (busy) return;
    setError('');
    const filters = {};
    for (const [name, label] of ranges) {
      const from = values[`${name}From`]?.trim();
      const to = values[`${name}To`]?.trim();
      if (from && to && from > to) {
        setError(`${label}: a kezdőérték nem lehet nagyobb a záróértéknél.`);
        return;
      }
      if (from || to) filters[name] = { ...(from ? { from } : {}), ...(to ? { to } : {}) };
    }
    const options = {};
    for (const name of ['exportDate', 'issuerBankAccount', 'invoiceForm']) {
      if (values[name]?.trim()) options[name] = values[name].trim();
    }
    setBusy(true);
    try {
      const blob = await exportInvoiceXml({ documentSeriesId: Number(values.documentSeriesId), filters, options });
      downloadFile(blob, blob.filename || 'vevoi_szamlak.xml');
    } catch (exportError) {
      let message = 'Az XML export nem sikerült.';
      if (exportError.response?.data instanceof Blob) {
        try { message = JSON.parse(await exportError.response.data.text()).error?.message || message; } catch (_) { /* Non-JSON response. */ }
      }
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 960, mx: 'auto' }}>
      <Box component="form" onSubmit={handleExport}>
        <Typography variant="h5" component="h1" sx={{ mb: 2 }}>Számla XML export</Typography>
          <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
            <Alert severity="info">A kiválasztott számlasorozat számlái és előlegszámlái közös XML-be kerülnek. Az intervallumok együtt érvényesülnek, és egyoldali határ is megadható.</Alert>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField select label="Számlasorozat" required fullWidth size="small" value={values.documentSeriesId}
              disabled={busy || loading} onChange={(event) => setValues((current) => ({ ...current, documentSeriesId: event.target.value }))}>
              {series.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
            </TextField>
            {ranges.map(([name, label, type]) => (
              <Box key={name} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                {field(`${name}From`, `${label} – tól`, type)}
                {field(`${name}To`, `${label} – ig`, type)}
              </Box>
            ))}
            {field('exportDate', 'Export dátuma (üresen: aktuális nap)', 'date')}
            {field('issuerBankAccount', 'Kibocsátó bankszámlaszáma (opcionális)')}
            {field('invoiceForm', 'Számla formája (opcionális)')}
          </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button type="submit" variant="contained" disabled={busy || loading || !values.documentSeriesId}>
            {busy ? 'Exportálás…' : 'XML letöltése'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default InvoiceXmlExportPage;
